/**
 * Hariom ERP - Settings Page
 * Company profile (editable + saved) and user management (add/edit/delete).
 */
import React, { useState } from 'react'
import { Settings2, Users, Shield, Building2, Bell, Save, Edit3, Trash2 } from 'lucide-react'
import toast from 'react-hot-toast'
import { useSettingsStore, useUsersStore, type AppUser, type CompanyProfile } from '../../store/settingsStore'
import FormModal, { type FormField } from '../../components/ui/FormModal'

const COMPANY_FIELDS: { key: keyof CompanyProfile; label: string }[] = [
  { key: 'name', label: 'Company Name' },
  { key: 'license', label: 'ERP License No' },
  { key: 'email', label: 'Primary Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'gst', label: 'GST No' },
  { key: 'cin', label: 'CIN' },
]

const USER_FIELDS: FormField[] = [
  { name: 'name',   label: 'Full Name', required: true, placeholder: 'e.g. Vikram Singh' },
  { name: 'email',  label: 'Email', required: true, placeholder: 'e.g. vikram@hariomepoxyshield.com', full: true },
  { name: 'role',   label: 'Role', type: 'select', options: ['admin', 'director', 'marketing', 'sales', 'production', 'inventory', 'finance', 'qc', 'hr', 'employee'] },
  { name: 'status', label: 'Status', type: 'select', options: ['active', 'inactive'] },
]

const Settings: React.FC = () => {
  const [tab, setTab] = useState('company')
  const { company, setCompany } = useSettingsStore()
  const [form, setForm] = useState<CompanyProfile>(company)

  const { items: users, add, update, remove } = useUsersStore()
  const [userModal, setUserModal] = useState<{ editing: AppUser | null } | null>(null)

  const saveCompany = () => { setCompany(form); toast.success('Company profile saved successfully') }

  const saveUser = (v: Record<string, string>) => {
    const email = v.email.trim()
    if (users.some(u => u.email.toLowerCase() === email.toLowerCase() && u.id !== userModal?.editing?.id)) {
      toast.error(`User "${email}" already exists`); return
    }
    const rec: AppUser = {
      id: userModal?.editing ? userModal.editing.id : `user-${Date.now()}`,
      name: v.name.trim(), email, role: v.role || 'employee', status: v.status || 'active',
    }
    if (userModal?.editing) { update(rec.id, rec); toast.success(`Updated ${rec.name}`) }
    else { add(rec); toast.success(`User added`) }
    setUserModal(null)
  }

  return (
    <div className="space-y-5">
      <div className="page-header">
        <div>
          <h1 className="page-title">Settings</h1>
          <p className="page-subtitle">Company profile, users, roles &amp; system configuration</p>
        </div>
      </div>

      <div className="flex gap-6">
        <div className="w-48 flex-shrink-0">
          <div className="card p-2 space-y-1">
            {[
              { id: 'company', icon: Building2, label: 'Company' },
              { id: 'users',   icon: Users,     label: 'Users' },
              { id: 'roles',   icon: Shield,    label: 'Roles' },
              { id: 'notif',   icon: Bell,      label: 'Notifications' },
            ].map(s => (
              <button key={s.id} onClick={() => setTab(s.id)}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                  tab === s.id ? 'bg-primary-50 text-primary-700 font-medium' : 'text-steel-600 hover:bg-steel-50'}`}>
                <s.icon size={15}/>{s.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1">
          {tab === 'company' && (
            <div className="card space-y-4">
              <h3 className="font-semibold text-steel-800 text-lg">Company Profile</h3>
              <div className="grid grid-cols-2 gap-4">
                {COMPANY_FIELDS.map(({ key, label }) => (
                  <div key={key}>
                    <label className="text-xs font-medium text-steel-600 block mb-1">{label}</label>
                    <input className="form-input" value={form[key]} onChange={e => setForm({ ...form, [key]: e.target.value })} />
                  </div>
                ))}
              </div>
              <div>
                <label className="text-xs font-medium text-steel-600 block mb-1">Head Office Address</label>
                <textarea className="form-input h-20 resize-none" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} />
              </div>
              <button onClick={saveCompany} className="btn-primary flex items-center gap-2 text-sm"><Save size={14}/> Save Changes</button>
            </div>
          )}

          {tab === 'users' && (
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-steel-800">User Management</h3>
                <button onClick={() => setUserModal({ editing: null })} className="btn-primary text-sm">+ Add User</button>
              </div>
              <div className="space-y-3">
                {users.length === 0 ? (
                  <p className="text-sm text-steel-400 text-center py-8">No users yet. Click <span className="font-semibold text-steel-600">“+ Add User”</span> to add one.</p>
                ) : users.map(u => (
                  <div key={u.id} className="flex items-center justify-between p-3 bg-steel-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                        <span className="text-xs font-bold text-primary-700">{u.name.charAt(0).toUpperCase()}</span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-steel-800">{u.name}</p>
                        <p className="text-xs text-steel-500">{u.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded capitalize">{u.role}</span>
                      <span className={u.status === 'active' ? 'badge-success' : 'bg-steel-200 text-steel-600 text-xs px-2 py-0.5 rounded-full'}>{u.status}</span>
                      <button onClick={() => setUserModal({ editing: u })} className="p-1.5 text-steel-500 hover:text-primary-600 hover:bg-white rounded" title="Edit"><Edit3 size={14}/></button>
                      <button onClick={() => { if (window.confirm(`Remove ${u.name}?`)) { remove(u.id); toast.success('Removed') } }} className="p-1.5 text-steel-500 hover:text-red-600 hover:bg-white rounded" title="Delete"><Trash2 size={14}/></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(tab === 'roles' || tab === 'notif') && (
            <div className="card flex items-center justify-center h-48 text-steel-400">
              <div className="text-center">
                <Settings2 size={32} className="mx-auto mb-2 opacity-40"/>
                <p className="text-sm">This section is coming soon</p>
                <p className="text-xs mt-1">Configure roles &amp; permissions for each module</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {userModal && (
        <FormModal
          title={userModal.editing ? 'Edit User' : 'Add User'}
          submitLabel={userModal.editing ? 'Save Changes' : 'Add User'}
          fields={USER_FIELDS}
          initial={userModal.editing ? { name: userModal.editing.name, email: userModal.editing.email, role: userModal.editing.role, status: userModal.editing.status } : {}}
          onClose={() => setUserModal(null)}
          onSave={saveUser}
        />
      )}
    </div>
  )
}

export default Settings
